import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCompanySettings } from '@/utils/companySettings';
import { jsPDF } from 'jspdf';
import {
  ArrowLeft, Plus, ChevronRight, ChevronDown, Layers, Home, 
  Package, Copy, Trash2, Edit2, Save, GripVertical, Search,
  FolderPlus, MoreVertical, Check, X, Download, FileText,
  DoorOpen, Bath, Utensils, Sofa, Bed, Car, Trees, Wrench, Ruler,
  BookmarkPlus, Star, Sparkles, Link2, ExternalLink, Image as ImageIcon,
  Calculator, Send, HelpCircle, FileDown, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { NextStepPrompt, TipsPanel, QuickHelpButton, PagePurpose } from '@/components/UXGuide';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Room type icons
const ROOM_ICONS = {
  living: Sofa,
  bedroom: Bed,
  bathroom: Bath,
  kitchen: Utensils,
  dining: Utensils,
  entry: DoorOpen,
  garage: Car,
  exterior: Trees,
  utility: Wrench,
  other: Home,
};

export default function ScopeBuilderPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // Dark mode state - sync with system preference
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('keyMadeDarkMode') === 'true';
    }
    return false;
  });
  
  const [project, setProject] = useState(null);
  const [scope, setScope] = useState({ rooms: [] });
  const [assemblies, setAssemblies] = useState([]);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRooms, setExpandedRooms] = useState({});
  const [availableTakeoffRooms, setAvailableTakeoffRooms] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  
  // Room description editing
  const [editingRoomDescription, setEditingRoomDescription] = useState(null);
  const [roomDescriptionText, setRoomDescriptionText] = useState('');
  
  // Dialogs
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAssemblyPicker, setShowAssemblyPicker] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showImportFromTakeoff, setShowImportFromTakeoff] = useState(false);
  const [showSaveAsAssembly, setShowSaveAsAssembly] = useState(false);
  const [showClipPicker, setShowClipPicker] = useState(false);
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  
  // AI Generation state
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiProjectType, setAiProjectType] = useState('residential_remodel');
  const [aiProjectDescription, setAiProjectDescription] = useState('');
  const [aiDetailLevel, setAiDetailLevel] = useState('detailed');
  const [aiGeneratedScope, setAiGeneratedScope] = useState(null);
  
  // Form state
  const [newRoom, setNewRoom] = useState({ name: '', type: 'other', floor: '1', sqft: 0 });
  const [newItem, setNewItem] = useState({ name: '', unit: 'EA', quantity: 1, notes: '' });
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [assemblySearchQuery, setAssemblySearchQuery] = useState('');
  const [clipSearchQuery, setClipSearchQuery] = useState('');
  const [newAssemblyName, setNewAssemblyName] = useState('');
  const [savingAssembly, setSavingAssembly] = useState(false);
  
  // Listen for dark mode changes from other components
  useEffect(() => {
    const handleStorage = () => {
      setDarkMode(localStorage.getItem('keyMadeDarkMode') === 'true');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(() => {
      const current = localStorage.getItem('keyMadeDarkMode') === 'true';
      if (current !== darkMode) setDarkMode(current);
    }, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [darkMode]);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, scopeRes, floorPlansRes, assembliesRes, clipsRes, companyRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/scope-builder`).catch(() => ({ data: null })),
        axios.get(`${API}/projects/${projectId}/floor-plans`).catch(() => ({ data: [] })),
        axios.get(`${API}/scope-builder-assemblies`).catch(() => ({ data: { assemblies: [] } })),
        axios.get(`${API}/clips?project_id=${projectId}`).catch(() => ({ data: [] })),
        getCompanySettings().then((d) => ({ data: d })).catch(() => ({ data: null }))
      ]);
      
      setProject(projectRes.data);
      setCompanySettings(companyRes.data);
      
      // Load scope data
      if (scopeRes.data && scopeRes.data.rooms) {
        setScope({ rooms: scopeRes.data.rooms || [] });
      }
      
      // Load assemblies from API
      setAssemblies(assembliesRes.data?.assemblies || []);
      
      // Load clips - map title to name for consistency
      const clipsData = Array.isArray(clipsRes.data) ? clipsRes.data : clipsRes.data?.clips || [];
      const mappedClips = clipsData.map(clip => ({
        ...clip,
        name: clip.name || clip.title,  // Use name if available, fallback to title
        image_url: clip.image_url || clip.primary_image || (clip.images && clip.images[0])
      }));
      setClips(mappedClips);
      
      // Extract rooms from floor plan polygons for import
      const plans = Array.isArray(floorPlansRes.data) ? floorPlansRes.data : floorPlansRes.data?.floor_plans || [];
      const takeoffRooms = [];
      plans.forEach(plan => {
        const polygons = plan.polygons || [];
        polygons.forEach(poly => {
          if (poly.label || poly.name) {
            takeoffRooms.push({
              id: poly.id,
              name: poly.label || poly.name,
              sqft: Math.round(poly.area || 0),
              floor: plan.floor || '1',
              type: guessRoomType(poly.label || poly.name),
              fromTakeoff: true,
              planId: plan.id
            });
          }
        });
      });
      setAvailableTakeoffRooms(takeoffRooms);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  // Guess room type from name
  const guessRoomType = (name) => {
    const n = name.toLowerCase();
    if (n.includes('bath') || n.includes('powder')) return 'bathroom';
    if (n.includes('bed') || n.includes('master') || n.includes('guest')) return 'bedroom';
    if (n.includes('kitchen')) return 'kitchen';
    if (n.includes('living') || n.includes('family') || n.includes('great')) return 'living';
    if (n.includes('dining')) return 'dining';
    if (n.includes('garage')) return 'garage';
    if (n.includes('entry') || n.includes('foyer')) return 'entry';
    if (n.includes('laundry') || n.includes('utility') || n.includes('mud')) return 'utility';
    return 'other';
  };

  const saveScope = async (newScope) => {
    try {
      await axios.post(`${API}/projects/${projectId}/scope-builder`, newScope);
      setScope(newScope);
    } catch (error) {
      console.error('Error saving scope:', error);
      toast.error('Failed to save scope');
    }
  };

  const handleAddRoom = () => {
    if (!newRoom.name) {
      toast.error('Please enter a room name');
      return;
    }

    const room = {
      id: `room-${Date.now()}`,
      ...newRoom,
      sqft: parseFloat(newRoom.sqft) || 0,
      items: []
    };

    const newScope = { ...scope, rooms: [...scope.rooms, room] };
    saveScope(newScope);
    setExpandedRooms(prev => ({ ...prev, [room.id]: true }));
    setShowAddRoom(false);
    setNewRoom({ name: '', type: 'other', floor: '1', sqft: 0 });
    toast.success(`Added ${room.name}`);
  };

  const handleDeleteRoom = (roomId) => {
    const newScope = { ...scope, rooms: scope.rooms.filter(r => r.id !== roomId) };
    saveScope(newScope);
    toast.success('Room deleted');
  };

  const handleAddItem = () => {
    if (!newItem.name || !selectedRoomId) {
      toast.error('Please enter an item name');
      return;
    }

    const item = {
      id: `item-${Date.now()}`,
      ...newItem,
      quantity: parseFloat(newItem.quantity) || 0
    };

    const newScope = {
      ...scope,
      rooms: scope.rooms.map(r => 
        r.id === selectedRoomId 
          ? { ...r, items: [...r.items, item] }
          : r
      )
    };
    saveScope(newScope);
    setShowAddItem(false);
    setNewItem({ name: '', unit: 'EA', quantity: 1, notes: '' });
    toast.success(`Added ${item.name}`);
  };

  const handleDeleteItem = (roomId, itemId) => {
    const newScope = {
      ...scope,
      rooms: scope.rooms.map(r => 
        r.id === roomId 
          ? { ...r, items: r.items.filter(i => i.id !== itemId) }
          : r
      )
    };
    saveScope(newScope);
  };

  const handleImportFromTakeoff = (selectedRooms) => {
    // Filter out rooms already in scope
    const existingIds = scope.rooms.map(r => r.fromTakeoffId);
    const newRooms = selectedRooms
      .filter(r => !existingIds.includes(r.id))
      .map(r => ({
        id: `room-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: r.name,
        type: r.type,
        floor: r.floor,
        sqft: r.sqft,
        items: [],
        fromTakeoffId: r.id,
        fromTakeoff: true
      }));

    if (newRooms.length === 0) {
      toast.info('All selected rooms are already in scope');
      return;
    }

    const newScope = { ...scope, rooms: [...scope.rooms, ...newRooms] };
    saveScope(newScope);
    
    // Auto-expand imported rooms
    const newExpanded = {};
    newRooms.forEach(r => { newExpanded[r.id] = true; });
    setExpandedRooms(prev => ({ ...prev, ...newExpanded }));
    
    setShowImportFromTakeoff(false);
    toast.success(`Imported ${newRooms.length} room(s) from Takeoff`);
  };

  const handleApplyAssembly = (assembly) => {
    if (!selectedRoomId) return;

    const room = scope.rooms.find(r => r.id === selectedRoomId);
    if (!room) return;

    const newItems = assembly.items.map(item => ({
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: item.name,
      unit: item.unit,
      quantity: item.defaultQty === 0 && item.unit === 'SF' ? room.sqft : item.defaultQty,
      notes: '',
      fromAssembly: assembly.id
    }));

    const newScope = {
      ...scope,
      rooms: scope.rooms.map(r => 
        r.id === selectedRoomId 
          ? { ...r, items: [...r.items, ...newItems] }
          : r
      )
    };
    saveScope(newScope);
    setShowAssemblyPicker(false);
    toast.success(`Applied "${assembly.name}" assembly with ${newItems.length} items`);
  };

  const handleDuplicateRoom = (room) => {
    const newRoom = {
      ...room,
      id: `room-${Date.now()}`,
      name: `${room.name} (Copy)`,
      items: room.items.map(item => ({
        ...item,
        id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }))
    };

    const newScope = { ...scope, rooms: [...scope.rooms, newRoom] };
    saveScope(newScope);
    toast.success(`Duplicated ${room.name}`);
  };

  const openClipPicker = (roomId, itemId) => {
    setSelectedRoomId(roomId);
    setSelectedItemId(itemId);
    setClipSearchQuery('');
    setShowClipPicker(true);
  };

  const handleLinkClip = (clip) => {
    const newScope = {
      ...scope,
      rooms: scope.rooms.map(room => {
        if (room.id !== selectedRoomId) return room;
        return {
          ...room,
          items: room.items.map(item => {
            if (item.id !== selectedItemId) return item;
            return {
              ...item,
              linkedClip: {
                id: clip.id,
                name: clip.name,
                spec_num: clip.spec_num,
                spec_category: clip.spec_category,
                image_url: clip.image_url,
                price: clip.price,
                vendor: clip.vendor
              }
            };
          })
        };
      })
    };
    
    saveScope(newScope);
    setShowClipPicker(false);
    toast.success(`Linked "${clip.spec_num || clip.name}" to scope item`);
  };

  const handleUnlinkClip = (roomId, itemId) => {
    const newScope = {
      ...scope,
      rooms: scope.rooms.map(room => {
        if (room.id !== roomId) return room;
        return {
          ...room,
          items: room.items.map(item => {
            if (item.id !== itemId) return item;
            const { linkedClip, ...rest } = item;
            return rest;
          })
        };
      })
    };
    
    saveScope(newScope);
    toast.success('Clip unlinked from scope item');
  };

  const openSaveAsAssembly = (room) => {
    setSelectedRoomId(room.id);
    setNewAssemblyName(`${room.name} Assembly`);
    setShowSaveAsAssembly(true);
  };

  const handleSaveAsAssembly = async () => {
    if (!newAssemblyName.trim()) {
      toast.error('Please enter a name for the assembly');
      return;
    }

    const room = scope.rooms.find(r => r.id === selectedRoomId);
    if (!room || room.items.length === 0) {
      toast.error('Room must have items to save as assembly');
      return;
    }

    setSavingAssembly(true);
    try {
      // Convert room items to assembly format
      const assemblyItems = room.items.map(item => ({
        name: item.name,
        unit: item.unit,
        defaultQty: item.unit === 'SF' || item.unit === 'LF' ? 0 : item.quantity
      }));

      const response = await axios.post(`${API}/scope-builder-assemblies`, {
        name: newAssemblyName.trim(),
        category: room.type,
        icon: room.type,
        items: assemblyItems,
        source_room: {
          name: room.name,
          sqft: room.sqft,
          itemCount: room.items.length
        }
      });

      // Add the new assembly to local state
      setAssemblies(prev => [...prev, response.data.assembly]);
      
      setShowSaveAsAssembly(false);
      setNewAssemblyName('');
      setSelectedRoomId(null);
      toast.success(`"${newAssemblyName}" saved as reusable assembly!`);
    } catch (error) {
      console.error('Error saving assembly:', error);
      toast.error('Failed to save assembly');
    } finally {
      setSavingAssembly(false);
    }
  };

  const handleDeleteAssembly = async (assemblyId) => {
    try {
      await axios.delete(`${API}/scope-builder-assemblies/${assemblyId}`);
      setAssemblies(prev => prev.filter(a => a.id !== assemblyId));
      toast.success('Assembly deleted');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Cannot delete default assemblies');
      } else {
        toast.error('Failed to delete assembly');
      }
    }
  };

  const handleSendToEstimate = async () => {
    if (scope.rooms.length === 0) {
      toast.error('No rooms in scope to send to estimate');
      return;
    }
    
    const totalItems = scope.rooms.reduce((sum, r) => sum + r.items.length, 0);
    if (totalItems === 0) {
      toast.error('No items in scope to send to estimate');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/projects/${projectId}/estimates/from-scope`, {
        name: `${project?.name || 'Project'} - Scope Estimate`,
        description: `Generated from Scope Builder with ${scope.rooms.length} rooms`
      });
      
      toast.success(`Estimate created with ${totalItems} items! Redirecting...`);
      
      // Navigate to the estimate page
      setTimeout(() => {
        navigate(`/projects/${projectId}/estimates/${response.data.estimate.id}`);
      }, 1000);
    } catch (error) {
      console.error('Error creating estimate from scope:', error);
      toast.error(error.response?.data?.detail || 'Failed to create estimate');
    }
  };

  // AI Scope Generation
  const handleAIGenerateScope = async () => {
    if (scope.rooms.length === 0) {
      toast.error('Add at least one room first to generate AI scope items');
      return;
    }

    setAiGenerating(true);
    setAiGeneratedScope(null);

    try {
      const response = await axios.post(`${API}/projects/${projectId}/scope-builder/ai-generate`, {
        project_type: aiProjectType,
        rooms: scope.rooms.map(r => ({
          name: r.name,
          type: r.type,
          sqft: r.sqft,
          takeoff_data: r.takeoff_data || {}
        })),
        project_description: aiProjectDescription,
        include_typical_items: true,
        detail_level: aiDetailLevel
      });

      if (response.data.success) {
        setAiGeneratedScope(response.data.generated_scope);
        toast.success('AI scope generated! Review and apply the suggestions.');
      } else {
        toast.error(response.data.error || 'Failed to generate scope');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAIGeneratedScope = () => {
    if (!aiGeneratedScope || !aiGeneratedScope.rooms) {
      toast.error('No generated scope to apply');
      return;
    }

    const now = new Date().toISOString();
    const newScope = { ...scope };

    // Match AI-generated rooms to existing rooms and add items
    aiGeneratedScope.rooms.forEach(aiRoom => {
      const existingRoom = newScope.rooms.find(
        r => r.name.toLowerCase() === aiRoom.room_name.toLowerCase()
      );

      if (existingRoom && aiRoom.scope_items) {
        // Add AI-generated items to existing room
        const existingItemNames = existingRoom.items.map(i => i.name.toLowerCase());
        
        aiRoom.scope_items.forEach(item => {
          // Only add if not already exists
          if (!existingItemNames.includes(item.name.toLowerCase())) {
            existingRoom.items.push({
              id: `ai-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              name: item.name,
              unit: item.unit || 'EA',
              quantity: item.unit === 'SF' ? (existingRoom.sqft || 1) : 
                        item.unit === 'LF' ? (existingRoom.takeoff_data?.perimeter_lf || 1) : 1,
              notes: item.notes || '',
              category: item.category || 'other',
              fromAI: true,
              created_at: now
            });
          }
        });
      }
    });

    saveScope(newScope);
    setShowAIGenerate(false);
    setAiGeneratedScope(null);
    toast.success('AI-generated scope items applied!');
  };

  // Room description handlers
  const openRoomDescriptionEditor = (room) => {
    setEditingRoomDescription(room.id);
    setRoomDescriptionText(room.description || '');
  };

  const saveRoomDescription = (roomId) => {
    const newScope = {
      ...scope,
      rooms: scope.rooms.map(r => 
        r.id === roomId 
          ? { ...r, description: roomDescriptionText }
          : r
      )
    };
    saveScope(newScope);
    setEditingRoomDescription(null);
    setRoomDescriptionText('');
    toast.success('Description saved');
  };

  const cancelRoomDescriptionEdit = () => {
    setEditingRoomDescription(null);
    setRoomDescriptionText('');
  };

  const exportToProposal = () => {
    // Generate scope summary text (for backward compatibility)
    let text = `SCOPE OF WORK - ${project?.name || 'Project'}\n`;
    text += `${'='.repeat(50)}\n\n`;

    scope.rooms.forEach(room => {
      text += `${room.name.toUpperCase()} (${room.sqft} SF)\n`;
      text += `${'-'.repeat(30)}\n`;
      room.items.forEach(item => {
        text += `  • ${item.name}: ${item.quantity} ${item.unit}`;
        if (item.notes) text += ` - ${item.notes}`;
        text += '\n';
      });
      text += '\n';
    });

    // Create download
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scope-${project?.name?.replace(/\s+/g, '-') || 'project'}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success('Scope exported');
    setShowExport(false);
  };

  // Generate PDF document (returns jsPDF doc object)
  const generatePdfDocument = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let y = margin;
    
    // Get branding from company settings
    const primaryColor = companySettings?.primary_color || '#BC5D41';
    const secondaryColor = companySettings?.secondary_color || '#1A1918';
    // DP67 Part 4b — the TENANT's name or none. Ours was the fallback firm on a
    // scope document a client reads.
    const companyName = companySettings?.company_name || '';
    const logoUrl = companySettings?.logo_url;
    
    // Parse hex color to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 188, g: 93, b: 65 };
    };
    
    const brandRgb = hexToRgb(primaryColor);
    const darkRgb = hexToRgb(secondaryColor);
    
    // Helper function to add a new page if needed
    const checkPageBreak = (requiredSpace = 20) => {
      if (y + requiredSpace > pageHeight - margin) {
        doc.addPage();
        y = margin;
        return true;
      }
      return false;
    };
    
    // Helper for text wrapping
    const addWrappedText = (text, x, maxWidth, lineHeight = 5) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      lines.forEach(line => {
        checkPageBreak(lineHeight);
        doc.text(line, x, y);
        y += lineHeight;
      });
    };
    
    // Helper to strip HTML tags for plain text output
    const stripHtml = (html) => {
      if (!html) return '';
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    };
    
    // ===== HEADER SECTION =====
    // Clean header with logo and project info (no colored banner)
    let headerHeight = 40;
    
    // Try to add company logo
    if (logoUrl) {
      try {
        // Add logo on the left (will be loaded asynchronously)
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`;
        
        // Create a promise to load the image
        await new Promise((resolve) => {
          img.onload = () => {
            try {
              // Calculate dimensions to fit within header
              const maxLogoHeight = 25;
              const maxLogoWidth = 60;
              let logoWidth = img.width;
              let logoHeight = img.height;
              
              // Scale to fit
              if (logoHeight > maxLogoHeight) {
                logoWidth = (logoWidth * maxLogoHeight) / logoHeight;
                logoHeight = maxLogoHeight;
              }
              if (logoWidth > maxLogoWidth) {
                logoHeight = (logoHeight * maxLogoWidth) / logoWidth;
                logoWidth = maxLogoWidth;
              }
              
              // Detect image format from URL
              const imgFormat = logoUrl.toLowerCase().endsWith('.jpg') || logoUrl.toLowerCase().endsWith('.jpeg') ? 'JPEG' : 'PNG';
              doc.addImage(img, imgFormat, margin, 10, logoWidth, logoHeight);
            } catch (e) {
              console.warn('Could not add logo to PDF:', e);
            }
            resolve();
          };
          img.onerror = () => {
            console.warn('Could not load logo for PDF');
            resolve();
          };
          // Timeout fallback
          setTimeout(resolve, 2000);
        });
      } catch (e) {
        console.warn('Logo loading error:', e);
      }
    }
    
    // Title on the right side
    doc.setTextColor(darkRgb.r, darkRgb.g, darkRgb.b);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('SCOPE OF WORK', pageWidth - margin, 15, { align: 'right' });
    
    // Project name and number - ALWAYS show both together
    // Use project_code (e.g., KC-RI15-2601) as the project identifier
    const projectCode = project?.project_code || project?.project_number || '';
    const projectTitle = projectCode 
      ? `${project?.name || 'Project'} • ${projectCode}`
      : (project?.name || 'Project');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(brandRgb.r, brandRgb.g, brandRgb.b);
    doc.text(projectTitle, pageWidth - margin, 24, { align: 'right' });
    
    // Divider line using brand color
    y = headerHeight + 5;
    doc.setDrawColor(brandRgb.r, brandRgb.g, brandRgb.b);
    doc.setLineWidth(1);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;
    
    // ===== PROJECT SUMMARY BOX =====
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(250, 249, 247); // #FAF9F7
    doc.rect(margin, y, pageWidth - 2 * margin, 25, 'F');
    doc.setDrawColor(229, 226, 221); // #E5E2DD
    doc.rect(margin, y, pageWidth - 2 * margin, 25, 'S');
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('PROJECT SUMMARY', margin + 5, y + 8);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Total Rooms: ${scope.rooms.length}`, margin + 5, y + 16);
    doc.text(`Total Items: ${totalItems}`, margin + 55, y + 16);
    doc.text(`Total Area: ${totalSqft.toLocaleString()} SF`, margin + 105, y + 16);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - margin - 35, y + 16);
    
    y += 35;
    
    // ===== ROOM SECTIONS =====
    const roomsByFloorForExport = {};
    scope.rooms.forEach(room => {
      const floor = room.floor || '1';
      if (!roomsByFloorForExport[floor]) roomsByFloorForExport[floor] = [];
      roomsByFloorForExport[floor].push(room);
    });
    
    Object.entries(roomsByFloorForExport).sort(([a], [b]) => a.localeCompare(b)).forEach(([floor, rooms]) => {
      checkPageBreak(20);
      
      // Floor Header
      doc.setFillColor(darkRgb.r, darkRgb.g, darkRgb.b);
      doc.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`FLOOR ${floor}`, margin + 5, y + 5.5);
      y += 12;
      
      rooms.forEach((room, roomIndex) => {
        checkPageBreak(35); // More space for description
        
        // Room Header
        doc.setTextColor(brandRgb.r, brandRgb.g, brandRgb.b);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(room.name.toUpperCase(), margin, y);
        
        doc.setTextColor(107, 105, 101); // #6B6965
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const roomInfo = `${room.sqft || 0} SF • ${room.items.length} items • ${room.type || 'other'}`;
        doc.text(roomInfo, pageWidth - margin - doc.getTextWidth(roomInfo), y);
        
        y += 3;
        doc.setDrawColor(brandRgb.r, brandRgb.g, brandRgb.b);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        y += 6;
        
        // Room Description (if present)
        if (room.description) {
          const cleanDescription = stripHtml(room.description);
          if (cleanDescription) {
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.setFont('helvetica', 'italic');
            addWrappedText(cleanDescription, margin + 3, pageWidth - 2 * margin - 6, 4);
            doc.setFont('helvetica', 'normal');
            y += 3;
          }
        }
        
        // Items Table Header
        doc.setFillColor(250, 249, 247);
        doc.rect(margin, y - 2, pageWidth - 2 * margin, 7, 'F');
        doc.setTextColor(107, 105, 101);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('ITEM', margin + 3, y + 3);
        doc.text('QTY', pageWidth - margin - 50, y + 3);
        doc.text('UNIT', pageWidth - margin - 25, y + 3);
        y += 8;
        
        // Items
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(26, 25, 24); // #1A1918
        room.items.forEach((item, itemIndex) => {
          checkPageBreak(8);
          
          // Alternating row colors
          if (itemIndex % 2 === 0) {
            doc.setFillColor(255, 255, 255);
          } else {
            doc.setFillColor(252, 251, 250);
          }
          doc.rect(margin, y - 3, pageWidth - 2 * margin, 6, 'F');
          
          doc.setFontSize(9);
          // Item name (with linked clip badge if available)
          let itemName = item.name;
          if (item.linkedClip?.spec_num) {
            itemName = `[${item.linkedClip.spec_num}] ${item.name}`;
          }
          const maxNameWidth = pageWidth - margin - 80;
          if (doc.getTextWidth(itemName) > maxNameWidth) {
            itemName = itemName.substring(0, 40) + '...';
          }
          doc.text(itemName, margin + 3, y);
          
          // Quantity and Unit
          doc.text(String(item.quantity || 0), pageWidth - margin - 50, y);
          doc.text(item.unit || 'EA', pageWidth - margin - 25, y);
          
          y += 6;
          
          // Notes if present
          if (item.notes) {
            doc.setFontSize(8);
            doc.setTextColor(107, 105, 101);
            addWrappedText(`Note: ${item.notes}`, margin + 8, pageWidth - 2 * margin - 10, 4);
            doc.setTextColor(26, 25, 24);
          }
        });
        
        y += 8;
      });
    });
    
    // DP67 Part 4b — footer is the tenant's name, or no footer line.
    const footerY = pageHeight - 15;
    doc.setFontSize(8);
    doc.setTextColor(107, 105, 101);
    doc.text(companyName ? `${companyName} • ${new Date().toLocaleString()}` : new Date().toLocaleString(), pageWidth / 2, footerY, { align: 'center' });
    
    return doc;
  };

  // Export scope as a professional PDF document
  const exportToPDF = async (preview = false) => {
    const doc = await generatePdfDocument();
    
    if (preview) {
      // Open in new tab for preview
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      toast.success('PDF preview opened in new tab');
    } else {
      // Save PDF with project code in filename
      const projectCode = project?.project_code || project?.project_number || '';
      const filename = projectCode 
        ? `Scope-${projectCode}-${project?.name?.replace(/\s+/g, '-') || 'Project'}.pdf`
        : `Scope-${project?.name?.replace(/\s+/g, '-') || 'Project'}.pdf`;
      doc.save(filename);
      toast.success('PDF exported successfully');
    }
    setShowExport(false);
  };

  const totalItems = scope.rooms.reduce((sum, r) => sum + r.items.length, 0);
  const totalSqft = scope.rooms.reduce((sum, r) => sum + (r.sqft || 0), 0);

  const filteredRooms = searchQuery 
    ? scope.rooms.filter(r => 
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.items.some(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : scope.rooms;

  // Group rooms by floor
  const roomsByFloor = filteredRooms.reduce((acc, room) => {
    const floor = room.floor || '1';
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(room);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]'}`}>
        <div className="animate-spin w-8 h-8 border-2 border-[#BC5D41] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D] text-white' : 'bg-[#FAF9F7] text-[#1A1918]'}`}>
      {/* Header */}
      <div className={`border-b sticky top-0 z-10 ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Breadcrumb Navigation */}
          <nav className={`flex items-center gap-2 text-sm mb-3 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
            <button
              onClick={() => navigate('/dashboard')}
              className={`hover:underline ${darkMode ? 'hover:text-white' : 'hover:text-[#1A1918]'}`}
            >
              My Desk
            </button>
            <span>/</span>
            <button
              onClick={() => navigate('/projects')}
              className={`hover:underline ${darkMode ? 'hover:text-white' : 'hover:text-[#1A1918]'}`}
            >
              Projects
            </button>
            <span>/</span>
            <button
              onClick={() => navigate(`/projects/${projectId}/hub`)}
              className={`hover:underline ${darkMode ? 'hover:text-white' : 'hover:text-[#1A1918]'}`}
            >
              {project?.name || 'Project'}
            </button>
            <span>/</span>
            <button
              onClick={() => navigate(`/projects/${projectId}/hub`)}
              className="hover:underline text-[#70170F] font-medium"
              data-testid="hub-back-link"
            >
              Hub
            </button>
            <span>/</span>
            <span className={darkMode ? 'text-white' : 'text-[#1A1918]'}>
              Scope Builder
            </span>
          </nav>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/projects/${projectId}`)}
                className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <HelpIcon elementId="scope-builder" position="inline" />
                <div>
                  <h1 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>
                    <Layers className="w-5 h-5 text-[#BC5D41]" />
                    Scope Builder
                  </h1>
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>
                    {project?.name} • {scope.rooms.length} rooms • {totalItems} items • {totalSqft.toLocaleString()} SF
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Search rooms or items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-9 w-64 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-white' : 'bg-white border-[#E5E2DD] text-[#1A1918]'}`}
                />
              </div>
              {availableTakeoffRooms.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setShowImportFromTakeoff(true)}
                  className="border-[#BC5D41] text-[#BC5D41] hover:bg-[#BC5D41]/10"
                >
                  <Ruler className="w-4 h-4 mr-2" />
                  Import from Takeoff ({availableTakeoffRooms.length})
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowAIGenerate(true)}
                className={`text-purple-500 hover:text-purple-600 hover:bg-purple-50 ${darkMode ? 'border-[#2A2A2A] hover:bg-purple-900/20' : 'border-[#E5E2DD]'}`}
                disabled={scope.rooms.length === 0}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Generate
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowExport(true)}
                className={darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button
                variant="outline"
                onClick={handleSendToEstimate}
                className={`text-[#10B981] hover:text-[#10B981] hover:bg-[#10B981]/10 ${darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}`}
                disabled={scope.rooms.length === 0}
              >
                <Calculator className="w-4 h-4 mr-2" />
                To Estimate
              </Button>
              <div className="relative">
                <QuickHelpButton currentStep="scope" darkMode={darkMode} />
              </div>
              <Button
                onClick={() => setShowAddRoom(true)}
                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Room
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Purpose - explains what this page is for */}
        <div className="mb-6">
          <PagePurpose currentStep="scope" darkMode={darkMode} />
        </div>
        
        {/* Next Step Prompt - shows when scope has items */}
        {scope.rooms.length > 0 && totalItems > 0 && (
          <div className="mb-6">
            <NextStepPrompt 
              currentStep="scope" 
              darkMode={darkMode}
              onNavigate={(step) => {
                if (step === 'bid-packages') {
                  navigate(`/projects/${projectId}/bid-packages`);
                } else if (step === 'estimate') {
                  handleSendToEstimate();
                }
              }}
            />
          </div>
        )}
        
        {scope.rooms.length === 0 ? (
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]'}>
            <CardContent className="py-12 text-center">
              <Layers className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-[#6B6965]'}`} />
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>No Rooms Yet</h3>
              <p className={`mb-4 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`}>Start building your scope by adding rooms</p>
              <Button onClick={() => setShowAddRoom(true)} className="bg-[#70170F] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add First Room
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(roomsByFloor).sort(([a], [b]) => a.localeCompare(b)).map(([floor, rooms]) => (
              <div key={floor}>
                <h2 className={`text-sm font-medium mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-[#6B6965]'}`}>
                  <Home className="w-4 h-4" />
                  Floor {floor}
                </h2>
                <div className="space-y-2">
                  {rooms.map(room => {
                    const RoomIcon = ROOM_ICONS[room.type] || Home;
                    const isExpanded = expandedRooms[room.id];

                    return (
                      <Collapsible
                        key={room.id}
                        open={isExpanded}
                        onOpenChange={(open) => setExpandedRooms(prev => ({ ...prev, [room.id]: open }))}
                      >
                        <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]'}>
                          <CollapsibleTrigger asChild>
                            <CardHeader className={`py-3 px-4 cursor-pointer transition-colors ${darkMode ? 'hover:bg-[#252525]' : 'hover:bg-[#FAF9F7]'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {isExpanded ? (
                                    <ChevronDown className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`} />
                                  ) : (
                                    <ChevronRight className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-[#6B6965]'}`} />
                                  )}
                                  <RoomIcon className="w-5 h-5 text-[#BC5D41]" />
                                  <div>
                                    <CardTitle className={`text-base font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>{room.name}</CardTitle>
                                    <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
                                      {room.sqft > 0 && `${room.sqft} SF • `}
                                      {room.items.length} item{room.items.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setSelectedRoomId(room.id);
                                      setShowAssemblyPicker(true);
                                    }}
                                    className="text-[#BC5D41]"
                                  >
                                    <Package className="w-4 h-4 mr-1" />
                                    Assembly
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <MoreVertical className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                                      <DropdownMenuItem onClick={() => handleDuplicateRoom(room)} className={darkMode ? 'text-white' : ''}>
                                        <Copy className="w-4 h-4 mr-2" />
                                        Duplicate Room
                                      </DropdownMenuItem>
                                      {room.items.length > 0 && (
                                        <DropdownMenuItem onClick={() => openSaveAsAssembly(room)} className={darkMode ? 'text-white' : ''}>
                                          <BookmarkPlus className="w-4 h-4 mr-2" />
                                          Save as Assembly
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuSeparator className={darkMode ? 'bg-[#2A2A2A]' : ''} />
                                      <DropdownMenuItem 
                                        onClick={() => handleDeleteRoom(room.id)}
                                        className="text-red-400"
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete Room
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <CardContent className="pt-0 pb-4 px-4">
                              <div className="ml-7 border-l border-[#2A2A2A] pl-4">
                                {/* Room Description Section */}
                                <div className={`mb-3 pb-3 border-b ${darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}`}>
                                  {editingRoomDescription === room.id ? (
                                    <div className="space-y-2">
                                      <Label className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Room Description</Label>
                                      <Textarea
                                        value={roomDescriptionText}
                                        onChange={(e) => setRoomDescriptionText(e.target.value)}
                                        placeholder="Describe the work to be done in this room... (Supports basic formatting)"
                                        rows={3}
                                        className={`text-sm ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-white' : 'bg-white border-[#E5E2DD]'}`}
                                      />
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() => saveRoomDescription(room.id)}
                                          className="bg-[#70170F] hover:bg-[#8B1D12] text-white h-7 px-2 text-xs"
                                        >
                                          <Save className="w-3 h-3 mr-1" />
                                          Save
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={cancelRoomDescriptionEdit}
                                          className={`h-7 px-2 text-xs ${darkMode ? 'text-gray-400' : ''}`}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-start justify-between gap-2">
                                      {room.description ? (
                                        <p className={`text-sm italic flex-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                          {room.description}
                                        </p>
                                      ) : (
                                        <p className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                          No description added
                                        </p>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openRoomDescriptionEditor(room)}
                                        className={`h-6 px-2 text-xs flex-shrink-0 ${darkMode ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
                                      >
                                        <Edit2 className="w-3 h-3 mr-1" />
                                        {room.description ? 'Edit' : 'Add Description'}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                
                                {room.items.length === 0 ? (
                                  <p className="text-sm text-[#6A6A6A] py-2">No items yet</p>
                                ) : (
                                  <div className="space-y-1">
                                    {room.items.map(item => (
                                      <div
                                        key={item.id}
                                        className={`flex items-center justify-between py-2 px-3 rounded group ${darkMode ? 'hover:bg-[#252525]' : 'hover:bg-[#FAF9F7]'}`}
                                      >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          <GripVertical className={`w-4 h-4 opacity-0 group-hover:opacity-100 flex-shrink-0 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <span className={`text-sm ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>{item.name}</span>
                                              {item.fromAssembly && (
                                                <Badge variant="outline" className="text-[10px] border-[#BC5D41]/30 text-[#BC5D41] flex-shrink-0">
                                                  Assembly
                                                </Badge>
                                              )}
                                            </div>
                                            {/* Show linked clip info */}
                                            {item.linkedClip && (
                                              <div className="flex items-center gap-2 mt-1">
                                                {item.linkedClip.image_url && (
                                                  <img 
                                                    src={item.linkedClip.image_url} 
                                                    alt="" 
                                                    className="w-6 h-6 rounded object-cover"
                                                  />
                                                )}
                                                <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] text-[10px] border-none font-mono">
                                                  {item.linkedClip.spec_num || item.linkedClip.spec_category || 'CLIP'}
                                                </Badge>
                                                <span className={`text-[10px] truncate max-w-[150px] ${darkMode ? 'text-[#6A6A6A]' : 'text-[#6B6965]'}`}>
                                                  {item.linkedClip.name}
                                                </span>
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-4 w-4 text-gray-500 hover:text-red-400"
                                                  onClick={() => handleUnlinkClip(room.id, item.id)}
                                                >
                                                  <X className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          <span className={`text-sm ${darkMode ? 'text-[#E5E2DD]' : 'text-[#6B6965]'}`}>
                                            {item.quantity} {item.unit}
                                          </span>
                                          {/* Link Clip button - ALWAYS VISIBLE */}
                                          {!item.linkedClip && clips.length > 0 && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className={`h-6 px-2 text-xs ${darkMode ? 'border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10' : 'border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10'}`}
                                              onClick={() => openClipPicker(room.id, item.id)}
                                            >
                                              <Link2 className="w-3 h-3 mr-1" />
                                              Link Material
                                            </Button>
                                          )}
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className={`h-6 w-6 opacity-0 group-hover:opacity-100 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                                            onClick={() => handleDeleteItem(room.id, item.id)}
                                          >
                                            <X className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`mt-2 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                                  onClick={() => {
                                    setSelectedRoomId(room.id);
                                    setShowAddItem(true);
                                  }}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Item
                                </Button>
                              </div>
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Room Dialog */}
      <Dialog open={showAddRoom} onOpenChange={setShowAddRoom}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Add Room</DialogTitle>
            <DialogDescription className="text-gray-400">Define a new room for your scope</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Room Name</Label>
              <Input
                placeholder="e.g., Master Bathroom"
                value={newRoom.name}
                onChange={(e) => setNewRoom(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Room Type</Label>
                <Select
                  value={newRoom.type}
                  onValueChange={(v) => setNewRoom(prev => ({ ...prev, type: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    <SelectItem value="living" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Living Room</SelectItem>
                    <SelectItem value="bedroom" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Bedroom</SelectItem>
                    <SelectItem value="bathroom" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Bathroom</SelectItem>
                    <SelectItem value="kitchen" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Kitchen</SelectItem>
                    <SelectItem value="dining" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Dining Room</SelectItem>
                    <SelectItem value="entry" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Entry/Foyer</SelectItem>
                    <SelectItem value="garage" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Garage</SelectItem>
                    <SelectItem value="exterior" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Exterior</SelectItem>
                    <SelectItem value="utility" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Utility/Laundry</SelectItem>
                    <SelectItem value="other" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Floor</Label>
                <Select
                  value={newRoom.floor}
                  onValueChange={(v) => setNewRoom(prev => ({ ...prev, floor: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    <SelectItem value="B" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Basement</SelectItem>
                    <SelectItem value="1" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">1st Floor</SelectItem>
                    <SelectItem value="2" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">2nd Floor</SelectItem>
                    <SelectItem value="3" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">3rd Floor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Square Footage</Label>
              <Input
                type="number"
                placeholder="0"
                value={newRoom.sqft}
                onChange={(e) => setNewRoom(prev => ({ ...prev, sqft: e.target.value }))}
                className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRoom(false)} className="border-[#2A2A2A] text-white hover:bg-[#2A2A2A]">
              Cancel
            </Button>
            <Button onClick={handleAddRoom} className="bg-[#70170F] text-white hover:bg-[#8B1D12]">
              Add Room
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Add Scope Item</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add to: {scope.rooms.find(r => r.id === selectedRoomId)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Item Name</Label>
              <Input
                placeholder="e.g., Tile Floor"
                value={newItem.name}
                onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Unit</Label>
                <Select
                  value={newItem.unit}
                  onValueChange={(v) => setNewItem(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                    <SelectItem value="EA" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Each (EA)</SelectItem>
                    <SelectItem value="SF" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Square Feet (SF)</SelectItem>
                    <SelectItem value="LF" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Linear Feet (LF)</SelectItem>
                    <SelectItem value="LS" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Lump Sum (LS)</SelectItem>
                    <SelectItem value="HR" className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">Hours (HR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300">Quantity</Label>
                <Input
                  type="number"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
                  className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Notes (optional)</Label>
              <Textarea
                placeholder="Any specifications or notes..."
                value={newItem.notes}
                onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)} className="border-[#2A2A2A] text-white hover:bg-[#2A2A2A]">
              Cancel
            </Button>
            <Button onClick={handleAddItem} className="bg-[#70170F] text-white hover:bg-[#8B1D12]">
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assembly Picker Dialog */}
      <Dialog open={showAssemblyPicker} onOpenChange={(open) => {
        setShowAssemblyPicker(open);
        if (!open) setAssemblySearchQuery('');
      }}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] max-w-xl text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-[#BC5D41]" />
              Apply Assembly
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Quickly add a set of standard items to {scope.rooms.find(r => r.id === selectedRoomId)?.name}
            </DialogDescription>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search assemblies..."
              value={assemblySearchQuery}
              onChange={(e) => setAssemblySearchQuery(e.target.value)}
              className="pl-9 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
            />
          </div>
          
          <div className="py-2 space-y-4 max-h-[400px] overflow-y-auto">
            {/* Default Assemblies Section */}
            {assemblies.filter(a => a.is_default && (
              !assemblySearchQuery || 
              a.name.toLowerCase().includes(assemblySearchQuery.toLowerCase()) ||
              a.category.toLowerCase().includes(assemblySearchQuery.toLowerCase())
            )).length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Star className="w-3 h-3" />
                  Default Assemblies
                </h4>
                <div className="space-y-2">
                  {assemblies
                    .filter(a => a.is_default && (
                      !assemblySearchQuery || 
                      a.name.toLowerCase().includes(assemblySearchQuery.toLowerCase()) ||
                      a.category.toLowerCase().includes(assemblySearchQuery.toLowerCase())
                    ))
                    .map(assembly => {
                      const AssemblyIcon = ROOM_ICONS[assembly.category] || Package;
                      return (
                        <div
                          key={assembly.id}
                          className="p-3 rounded-lg border border-[#2A2A2A] hover:border-[#BC5D41] cursor-pointer transition-colors group"
                          onClick={() => handleApplyAssembly(assembly)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-[#2A2A2A] group-hover:bg-[#BC5D41]/20">
                                <AssemblyIcon className="w-4 h-4 text-[#BC5D41]" />
                              </div>
                              <div>
                                <h4 className="font-medium text-sm">{assembly.name}</h4>
                                <p className="text-xs text-gray-500">{assembly.items.length} items • {assembly.category}</p>
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            
            {/* Custom Assemblies Section */}
            {assemblies.filter(a => a.is_custom && (
              !assemblySearchQuery || 
              a.name.toLowerCase().includes(assemblySearchQuery.toLowerCase()) ||
              a.category.toLowerCase().includes(assemblySearchQuery.toLowerCase())
            )).length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  Your Custom Assemblies
                </h4>
                <div className="space-y-2">
                  {assemblies
                    .filter(a => a.is_custom && (
                      !assemblySearchQuery || 
                      a.name.toLowerCase().includes(assemblySearchQuery.toLowerCase()) ||
                      a.category.toLowerCase().includes(assemblySearchQuery.toLowerCase())
                    ))
                    .map(assembly => {
                      const AssemblyIcon = ROOM_ICONS[assembly.category] || Package;
                      return (
                        <div
                          key={assembly.id}
                          className="p-3 rounded-lg border border-[#2A2A2A] hover:border-[#BC5D41] cursor-pointer transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div 
                              className="flex items-center gap-3 flex-1"
                              onClick={() => handleApplyAssembly(assembly)}
                            >
                              <div className="p-2 rounded-lg bg-gradient-to-br from-[#BC5D41]/20 to-[#70170F]/20">
                                <AssemblyIcon className="w-4 h-4 text-[#BC5D41]" />
                              </div>
                              <div>
                                <h4 className="font-medium text-sm flex items-center gap-2">
                                  {assembly.name}
                                  <Badge className="bg-[#BC5D41]/20 text-[#BC5D41] text-[10px] border-none">Custom</Badge>
                                </h4>
                                <p className="text-xs text-gray-500">{assembly.items.length} items • {assembly.category}</p>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100">
                                  <MoreVertical className="w-3 h-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-[#2A2A2A]">
                                <DropdownMenuItem 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAssembly(assembly.id);
                                  }}
                                  className="text-red-400"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Assembly
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
            
            {/* Empty State */}
            {assemblies.filter(a => 
              !assemblySearchQuery || 
              a.name.toLowerCase().includes(assemblySearchQuery.toLowerCase()) ||
              a.category.toLowerCase().includes(assemblySearchQuery.toLowerCase())
            ).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No assemblies found</p>
                {assemblySearchQuery && (
                  <p className="text-sm mt-1">Try a different search term</p>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t border-[#2A2A2A] pt-4">
            <p className="text-xs text-gray-500 flex-1">
              Tip: Save any room as a custom assembly using the room menu
            </p>
            <Button variant="outline" onClick={() => setShowAssemblyPicker(false)} className="border-[#2A2A2A] text-white hover:bg-[#2A2A2A]">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save as Assembly Dialog */}
      <Dialog open={showSaveAsAssembly} onOpenChange={setShowSaveAsAssembly}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5 text-[#BC5D41]" />
              Save as Assembly
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Save this room&apos;s items as a reusable assembly template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Assembly Name</Label>
              <Input
                placeholder="e.g., Master Bath Standard"
                value={newAssemblyName}
                onChange={(e) => setNewAssemblyName(e.target.value)}
                className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
              />
            </div>
            
            {/* Preview */}
            {selectedRoomId && (
              <div>
                <Label className="text-gray-300">Items to Include</Label>
                <div className="mt-2 p-3 rounded-lg bg-[#0D0D0D] border border-[#2A2A2A] max-h-48 overflow-y-auto">
                  {scope.rooms.find(r => r.id === selectedRoomId)?.items.map((item, idx) => (
                    <div key={item.id} className="flex items-center justify-between py-1 text-sm">
                      <span className="text-gray-300">{item.name}</span>
                      <span className="text-gray-500">{item.quantity} {item.unit}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  SF/LF quantities will be auto-calculated when applied to new rooms
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSaveAsAssembly(false)} 
              className="border-[#2A2A2A] text-white hover:bg-[#2A2A2A]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAsAssembly} 
              disabled={savingAssembly}
              className="bg-[#70170F] text-white hover:bg-[#8B1D12]"
            >
              {savingAssembly ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <BookmarkPlus className="w-4 h-4 mr-2" />
                  Save Assembly
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-[#BC5D41]" />
              Export Scope of Work
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Download your scope document for proposals and contractor communication
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {/* Summary Card */}
            <Card className="bg-[#0D0D0D] border-[#2A2A2A]">
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <Layers className="w-8 h-8 text-[#BC5D41]" />
                  <div>
                    <p className="font-medium text-white">{project?.name || 'Project'}</p>
                    <p className="text-sm text-gray-500">
                      {scope.rooms.length} rooms • {totalItems} items • {totalSqft.toLocaleString()} SF
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Export Options */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Export Format</Label>
              
              {/* PDF Options */}
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-1">PDF Document (Recommended)</p>
                <div className="flex gap-2">
                  {/* Preview PDF */}
                  <button
                    onClick={() => exportToPDF(true)}
                    className="flex-1 flex items-center gap-3 p-3 rounded-lg border border-[#2A2A2A] bg-[#0D0D0D] hover:bg-[#1A1A1A] hover:border-[#BC5D41]/50 transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-[#3B82F6]/20 group-hover:bg-[#3B82F6]/30">
                      <Eye className="w-4 h-4 text-[#3B82F6]" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-white text-sm">Preview</p>
                      <p className="text-[10px] text-gray-500">Opens in new tab</p>
                    </div>
                  </button>
                  
                  {/* Download PDF */}
                  <button
                    onClick={() => exportToPDF(false)}
                    className="flex-1 flex items-center gap-3 p-3 rounded-lg border border-[#2A2A2A] bg-[#0D0D0D] hover:bg-[#1A1A1A] hover:border-[#BC5D41]/50 transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-[#70170F]/20 group-hover:bg-[#70170F]/30">
                      <FileDown className="w-4 h-4 text-[#BC5D41]" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-white text-sm">Download</p>
                      <p className="text-[10px] text-gray-500">Save to device</p>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* TXT Option */}
              <button
                onClick={exportToProposal}
                className="w-full flex items-center gap-4 p-3 rounded-lg border border-[#2A2A2A] bg-[#0D0D0D] hover:bg-[#1A1A1A] hover:border-[#2A2A2A] transition-colors group"
              >
                <div className="p-2 rounded-lg bg-[#2A2A2A] group-hover:bg-[#3A3A3A]">
                  <FileText className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-300">Plain Text</p>
                  <p className="text-xs text-gray-500">Simple text format for copy/paste</p>
                </div>
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExport(false)} className="border-[#2A2A2A] text-gray-300">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Takeoff Dialog */}
      <Dialog open={showImportFromTakeoff} onOpenChange={setShowImportFromTakeoff}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ruler className="w-5 h-5 text-[#BC5D41]" />
              Import from Takeoff
            </DialogTitle>
            <DialogDescription>
              Select rooms from your floor plan takeoff to add to scope. Square footage will be auto-populated.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 max-h-[400px] overflow-y-auto">
            {availableTakeoffRooms.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Ruler className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No rooms found in Takeoff</p>
                <p className="text-sm">Draw room polygons in the Takeoff tool first</p>
              </div>
            ) : (
              availableTakeoffRooms.map(room => {
                const RoomIcon = ROOM_ICONS[room.type] || Home;
                const alreadyImported = scope.rooms.some(r => r.fromTakeoffId === room.id);
                return (
                  <div
                    key={room.id}
                    className={`p-3 rounded-lg border transition-colors ${
                      alreadyImported 
                        ? 'border-green-500/30 bg-green-500/10 cursor-not-allowed'
                        : 'border-[#2A2A2A] hover:border-[#BC5D41] cursor-pointer'
                    }`}
                    onClick={() => !alreadyImported && handleImportFromTakeoff([room])}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <RoomIcon className={`w-5 h-5 ${alreadyImported ? 'text-green-500' : 'text-[#BC5D41]'}`} />
                        <div>
                          <h4 className="font-medium text-sm">{room.name}</h4>
                          <p className="text-xs text-gray-500">Floor {room.floor} • {room.sqft} SF</p>
                        </div>
                      </div>
                      {alreadyImported ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <Check className="w-3 h-3 mr-1" />
                          Imported
                        </Badge>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-[#BC5D41]">
                          <Plus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportFromTakeoff(false)} className="border-[#2A2A2A]">
              Done
            </Button>
            {availableTakeoffRooms.filter(r => !scope.rooms.some(sr => sr.fromTakeoffId === r.id)).length > 0 && (
              <Button 
                onClick={() => handleImportFromTakeoff(availableTakeoffRooms.filter(r => !scope.rooms.some(sr => sr.fromTakeoffId === r.id)))}
                className="bg-[#70170F]"
              >
                Import All ({availableTakeoffRooms.filter(r => !scope.rooms.some(sr => sr.fromTakeoffId === r.id)).length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clip Picker Dialog */}
      <Dialog open={showClipPicker} onOpenChange={(open) => {
        setShowClipPicker(open);
        if (!open) setClipSearchQuery('');
      }}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] max-w-2xl text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#3B82F6]" />
              Link Material/Selection
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Connect a clip (material selection) to this scope item for SpecNum tracking
            </DialogDescription>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search clips by name, SpecNum, or category..."
              value={clipSearchQuery}
              onChange={(e) => setClipSearchQuery(e.target.value)}
              className="pl-9 bg-[#0D0D0D] border-[#2A2A2A] text-white placeholder:text-gray-500"
            />
          </div>
          
          <div className="py-2 max-h-[400px] overflow-y-auto">
            {clips.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No clips in this project yet</p>
                <p className="text-sm mt-1">Add materials using the web clipper or manual entry</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {clips
                  .filter(clip => {
                    if (!clipSearchQuery) return true;
                    const q = clipSearchQuery.toLowerCase();
                    return (
                      clip.name?.toLowerCase().includes(q) ||
                      clip.spec_num?.toLowerCase().includes(q) ||
                      clip.spec_category?.toLowerCase().includes(q) ||
                      clip.vendor?.toLowerCase().includes(q)
                    );
                  })
                  .map(clip => (
                    <div
                      key={clip.id}
                      className="p-3 rounded-lg border border-[#2A2A2A] hover:border-[#3B82F6] cursor-pointer transition-colors group"
                      onClick={() => handleLinkClip(clip)}
                    >
                      <div className="flex gap-3">
                        {clip.image_url ? (
                          <img 
                            src={clip.image_url} 
                            alt="" 
                            className="w-16 h-16 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded bg-[#252525] flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="w-6 h-6 text-gray-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {clip.spec_num && (
                              <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] text-[10px] border-none font-mono">
                                {clip.spec_num}
                              </Badge>
                            )}
                            {clip.spec_category && !clip.spec_num && (
                              <Badge className="bg-[#6B7280]/20 text-[#9CA3AF] text-[10px] border-none">
                                {clip.spec_category}
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-sm text-[#E5E2DD] truncate">{clip.name}</h4>
                          <p className="text-xs text-[#6A6A6A] truncate">
                            {clip.vendor && `${clip.vendor} • `}
                            {clip.price && `$${clip.price}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            
            {clips.length > 0 && clips.filter(clip => {
              if (!clipSearchQuery) return true;
              const q = clipSearchQuery.toLowerCase();
              return (
                clip.name?.toLowerCase().includes(q) ||
                clip.spec_num?.toLowerCase().includes(q) ||
                clip.spec_category?.toLowerCase().includes(q)
              );
            }).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No clips match your search</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t border-[#2A2A2A] pt-4">
            <p className="text-xs text-[#6A6A6A] flex-1">
              {clips.length} clip{clips.length !== 1 ? 's' : ''} available
            </p>
            <Button variant="outline" onClick={() => setShowClipPicker(false)} className="border-[#2A2A2A] text-white hover:bg-[#2A2A2A]">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Scope Dialog */}
      <Dialog open={showAIGenerate} onOpenChange={setShowAIGenerate}>
        <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI Scope Generation
            </DialogTitle>
            <DialogDescription className={darkMode ? 'text-gray-400' : ''}>
              Use AI to automatically generate scope of work items for your rooms. 
              The AI will analyze each room and suggest typical work items based on the project type.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Project Type */}
            <div className="space-y-2">
              <Label className={darkMode ? 'text-gray-300' : ''}>Project Type</Label>
              <Select value={aiProjectType} onValueChange={setAiProjectType}>
                <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-white' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  <SelectItem value="residential_remodel" className={darkMode ? 'text-white' : ''}>Residential Remodel</SelectItem>
                  <SelectItem value="new_construction" className={darkMode ? 'text-white' : ''}>New Construction</SelectItem>
                  <SelectItem value="commercial_tenant_improvement" className={darkMode ? 'text-white' : ''}>Commercial Tenant Improvement</SelectItem>
                  <SelectItem value="kitchen_bath_remodel" className={darkMode ? 'text-white' : ''}>Kitchen & Bath Remodel</SelectItem>
                  <SelectItem value="addition" className={darkMode ? 'text-white' : ''}>Addition</SelectItem>
                  <SelectItem value="interior_design" className={darkMode ? 'text-white' : ''}>Interior Design Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Detail Level */}
            <div className="space-y-2">
              <Label className={darkMode ? 'text-gray-300' : ''}>Detail Level</Label>
              <Select value={aiDetailLevel} onValueChange={setAiDetailLevel}>
                <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-white' : ''}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  <SelectItem value="basic" className={darkMode ? 'text-white' : ''}>Basic - Major items only</SelectItem>
                  <SelectItem value="standard" className={darkMode ? 'text-white' : ''}>Standard - Common items</SelectItem>
                  <SelectItem value="detailed" className={darkMode ? 'text-white' : ''}>Detailed - Comprehensive list</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Additional Description */}
            <div className="space-y-2">
              <Label className={darkMode ? 'text-gray-300' : ''}>Additional Project Details (Optional)</Label>
              <Textarea
                value={aiProjectDescription}
                onChange={(e) => setAiProjectDescription(e.target.value)}
                placeholder="Describe any specific requirements, finishes, or special considerations..."
                rows={3}
                className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-white' : ''}
              />
            </div>

            {/* Rooms to Process */}
            <div className="space-y-2">
              <Label className={darkMode ? 'text-gray-300' : ''}>Rooms to Generate Scope For</Label>
              <div className={`rounded-lg border p-3 max-h-40 overflow-y-auto ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : 'bg-gray-50 border-gray-200'}`}>
                {scope.rooms.length === 0 ? (
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    No rooms added yet. Add rooms first to generate scope.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {scope.rooms.map(room => {
                      const RoomIcon = ROOM_ICONS[room.type] || Home;
                      return (
                        <div key={room.id} className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : ''}`}>
                          <RoomIcon className="w-4 h-4 text-[#BC5D41]" />
                          <span>{room.name}</span>
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            ({room.sqft || 0} SF, {room.items.length} existing items)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Generated Results Preview */}
            {aiGeneratedScope && (
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  <Check className="w-4 h-4" />
                  AI Generated Scope (Preview)
                </Label>
                <div className={`rounded-lg border p-3 max-h-60 overflow-y-auto ${darkMode ? 'bg-[#0D0D0D] border-green-800' : 'bg-green-50 border-green-200'}`}>
                  {aiGeneratedScope.rooms?.map((room, idx) => (
                    <div key={idx} className="mb-3">
                      <h4 className={`font-medium text-sm ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                        {room.room_name}
                      </h4>
                      <ul className={`text-xs ml-4 mt-1 space-y-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {room.scope_items?.slice(0, 5).map((item, i) => (
                          <li key={i}>• {item.name} ({item.unit})</li>
                        ))}
                        {room.scope_items?.length > 5 && (
                          <li className="italic">...and {room.scope_items.length - 5} more items</li>
                        )}
                      </ul>
                    </div>
                  ))}
                  {aiGeneratedScope.general_conditions?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-dashed">
                      <h4 className={`font-medium text-sm ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                        General Conditions
                      </h4>
                      <ul className={`text-xs ml-4 mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {aiGeneratedScope.general_conditions.slice(0, 3).map((item, i) => (
                          <li key={i}>• {item.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className={`border-t pt-4 ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAIGenerate(false);
                setAiGeneratedScope(null);
              }}
              className={darkMode ? 'border-[#2A2A2A] text-white' : ''}
            >
              Cancel
            </Button>
            {!aiGeneratedScope ? (
              <Button
                onClick={handleAIGenerateScope}
                disabled={aiGenerating || scope.rooms.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {aiGenerating ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Scope
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={applyAIGeneratedScope}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Apply to Rooms
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
